import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Flex, Modal, Spin, Typography } from 'antd';
import { GoogleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface LoginModalProps {
  open: boolean;
  clientId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

export default function LoginModal({ open, clientId, onCancel, onSuccess }: LoginModalProps) {
  const { t } = useTranslation(['status', 'common']);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [gisReady, setGisReady] = useState(false);

  // Wait for the GIS script to load
  useEffect(() => {
    if (!open) return;
    setError(null);
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      if (window.google?.accounts?.oauth2) {
        setGisReady(true);
      } else {
        setTimeout(check, 100);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [open]);

  const handleSignIn = useCallback(() => {
    if (!window.google?.accounts?.oauth2) return;
    setError(null);

    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: 'openid email profile',
      prompt: 'select_account',
      callback: async (response) => {
        if (response.error || !response.access_token) {
          setError(response.error ?? t('status:login.cancelled'));
          return;
        }
        setSubmitting(true);
        try {
          const resp = await fetch('/auth/google-token', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accessToken: response.access_token }),
          });
          if (!resp.ok) {
            const body = await resp.json().catch(() => ({}));
            throw new Error(body.error || `HTTP ${resp.status}`);
          }
          onSuccess();
        } catch (e) {
          setError(e instanceof Error ? e.message : t('status:login.failed'));
        } finally {
          setSubmitting(false);
        }
      },
      error_callback: (err) => {
        if (err.type !== 'popup_closed') {
          setError(err.message ?? err.type);
        }
      },
    });
    tokenClient.requestAccessToken();
  }, [clientId, onSuccess, t]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      title={t('common:auth.signIn')}
      destroyOnHidden
      width={360}
    >
      <Flex vertical align="center" gap="middle" style={{ padding: '8px 0 12px' }}>
        <Text type="secondary">{t('status:login.subtitle')}</Text>
        {error && <Alert type="error" title={error} style={{ width: '100%' }} />}
        {submitting ? (
          <Flex justify="center" style={{ padding: 12 }}>
            <Spin />
          </Flex>
        ) : (
          <Button
            size="large"
            icon={<GoogleOutlined />}
            onClick={handleSignIn}
            disabled={!gisReady}
            style={{ minWidth: 240 }}
          >
            {t('status:login.googleButton')}
          </Button>
        )}
      </Flex>
    </Modal>
  );
}
